/**
 * Authentication Service - Legacy Compatibility Layer
 *
 * Delegates to @agion/auth. Kept for backward compatibility with
 * Sidebar.tsx and ProfilePage.tsx which use authService.getUserProfile().
 *
 * IMPORTANT: All authorization (RBAC) is enforced server-side ONLY.
 */

import { AuthClient } from '@agion/auth'
import type { AuthUser } from '@agion/auth'
import { config } from '../utils/config'
import { forgetAllFailedSends } from '../utils/failedSendStore'
import { forgetAllLaunchValues } from '../utils/launchValuesStore'

// Extended profile with legacy snake_case organization_id
interface UserProfile extends Omit<AuthUser, 'organizationId'> {
  organization_id: string
}

// Shared client instance for direct session checks
const authClient = new AuthClient({
  authUrl: config.authServiceUrl,
  apiUrl: config.apiUrl,
  loginUrl: config.loginUrl,
  appName: config.oidcClientId || 'chat-ui',
})

class AuthService {
  async getUserProfile(): Promise<UserProfile | null> {
    try {
      const result = await authClient.checkSession()
      // No profile without a signed-in session. An unavailable sign-in
      // service is no profile too; it never signs the user out here.
      if (result.status !== 'ok') return null

      const { user } = result
      return {
        ...user,
        organization_id: user.organizationId ?? '',
      }
    } catch (error) {
      console.error('Failed to get user profile from session:', error)
      return null
    }
  }

  async logout() {
    // Sends and start values kept in this tab go before the tab changes hands.
    forgetAllFailedSends()
    forgetAllLaunchValues()
    await authClient.logout()
  }
}

export const authService = new AuthService()
